'use client';

import { c as _c } from "react/compiler-runtime";
import { ClientOnly } from '@/components/ClientOnly';
import { PencilIcon } from '@/components/Dashboard/icons/PencilIcon';
import { Download } from '@/components/DetailedView/Download';
import { MetricWarning } from '@/components/DetailedView/MetricWarning';
import { Share } from '@/components/DetailedView/Share';
import { MaximizeIcon } from '@/components/DetailedView/icons/MaximizeIcon';
import { metricStatusText } from '@/components/DetailedView/utils';
import { MetricStatus } from '@/components/Metrics/MetricCard';
import { Notification, NotificationProvider } from '@/components/Notification';
import RichText from '@/components/RichText';
import { Skeleton } from '@/components/Skeleton';
import { METRICS_PATH, SEARCH_CONSTANTS, SHARE_METRICS_PATH } from '@/constants';
import { formatDate } from '@/lib/utils/date';
import { getClientSideURL } from '@/lib/utils/getURL';
import { isMedia } from '@/payload/lib/isMedia';
import { type Metric } from '@/payload/payload.types';
import { Chart } from '@tallinn-arvudes/ui/chart';
import { parseChartData } from '@tallinn-arvudes/ui/chart/utils';
import { Button } from '@tallinn-arvudes/ui/tallinn-design/Button';
import { Chip } from '@tallinn-arvudes/ui/tallinn-design/Chip';
import { Link } from '@tallinn-arvudes/ui/tallinn-design/Link';
import { cn } from '@tallinn-arvudes/ui/utils';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import React, { useRef, type ReactNode } from 'react';
const InfoBlock = t0 => {
  const $ = _c(5);
  const {
    children,
    title
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <p className="text-semantic-content-subtle text-[1rem]">{title}</p>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div>{t1}{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const DetailedView = t0 => {
  const $ = _c(105);
  const {
    metric,
    loggedIn,
    inSideModal: t1
  } = t0;
  const inSideModal = t1 === undefined ? false : t1;
  const router = useRouter();
  const containerRef = useRef(null);
  let t2;
  if ($[0] !== metric) {
    t2 = parseChartData(metric);
    $[0] = metric;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const parsedMetric = t2;
  let t3;
  if ($[2] !== metric.image) {
    t3 = isMedia(metric.image);
    $[2] = metric.image;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const media = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Notification successClassName="xl:w-3/5 whitespace-pre-wrap" isDismissible={true} />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== loggedIn || $[6] !== metric) {
    t5 = loggedIn && <MetricWarning className="px-10 pt-5 -mb-5" metric={metric} />;
    $[5] = loggedIn;
    $[6] = metric;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <sub className="uppercase text-xs text-semantic-content-brand">MÕÕDIK</sub>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = metric.titlePlainText || "";
  let t8;
  if ($[9] !== metric.title) {
    t8 = <RichText className="break-words" data={metric.title} />;
    $[9] = metric.title;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t7 || $[12] !== t8) {
    t9 = <div className="text-semantic-content-brand space-y-3">{t6}<h1 className="tds-heading-lg" title={t7}>{t8}</h1></div>;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <sub className="uppercase text-xs text-semantic-content-brand hidden lg:inline">TREND</sub>;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== metric.trendArrowBackground || $[16] !== metric.trendArrowDirection) {
    t11 = <MetricStatus direction={metric.trendArrowDirection} background={metric.trendArrowBackground} className="w-12 h-12 lg:w-20 lg:h-20" />;
    $[15] = metric.trendArrowBackground;
    $[16] = metric.trendArrowDirection;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== metric) {
    t12 = metricStatusText(metric);
    $[18] = metric;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t12) {
    t13 = <p className="font-bold text-xl md:text-2xl lg:text-3xl">{t12}</p>;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t11 || $[23] !== t13) {
    t14 = <div className="space-y-2 lg:space-y-4">{t10}<div className="flex flex-row items-center gap-5">{t11}{t13}</div></div>;
    $[22] = t11;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== inSideModal) {
    t15 = cn("absolute top-0 right-0 !mt-4 lg:!mt-8 !mr-6 flex items-center gap-1", {
      "!mr-[4rem] md:!mr-6": inSideModal
    });
    $[25] = inSideModal;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== loggedIn || $[28] !== metric.id) {
    t16 = loggedIn && <Button compact={true} variant="icon" tooltip="Redigeeri" target="_blank" rel="noopener noreferrer" className="text-semantic-content-faint" href={`/admin/collections/metrics/${metric.id}`}><PencilIcon className="!w-5 lg:!w-6 !h-5 lg:!h-6" /></Button>;
    $[27] = loggedIn;
    $[28] = metric.id;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== inSideModal || $[31] !== metric.id) {
    t17 = inSideModal && <Button compact={true} variant="icon" tooltip="Suurenda" className="text-semantic-content-faint" onClick={() => {
      window.location.replace(`${METRICS_PATH}/${metric.id}`);
    }}><MaximizeIcon className="!w-5 lg:!w-6 !h-5 lg:!h-6" /></Button>;
    $[30] = inSideModal;
    $[31] = metric.id;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== metric.id || $[34] !== metric.titlePlainText) {
    t18 = <Share getIframeProps={() => {
      const title = metric.titlePlainText;
      const src = `${getClientSideURL()}${SHARE_METRICS_PATH}/${metric.id}`;
      return {
        title,
        src
      };
    }} containerRef={containerRef} />;
    $[33] = metric.id;
    $[34] = metric.titlePlainText;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== metric || $[37] !== parsedMetric) {
    t19 = <Download containerRef={containerRef} metric={metric} parsedMetric={parsedMetric} />;
    $[36] = metric;
    $[37] = parsedMetric;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t15 || $[40] !== t16 || $[41] !== t17 || $[42] !== t18 || $[43] !== t19) {
    t20 = <div className={t15}>{t16}{t17}{t18}{t19}</div>;
    $[39] = t15;
    $[40] = t16;
    $[41] = t17;
    $[42] = t18;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t14 || $[46] !== t20 || $[47] !== t9) {
    t21 = <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-7 px-4 lg:px-10 pt-5 lg:pt-10">{t9}{t14}{t20}</div>;
    $[45] = t14;
    $[46] = t20;
    $[47] = t9;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  const t22 = metric.focusArea ?? undefined;
  let t23;
  if ($[49] !== t22) {
    t23 = {
      size: "md",
      highLightedZone: t22
    };
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== parsedMetric || $[52] !== t23) {
    t24 = <div className="grow h-[300px] md:h-[450px]"><Chart options={t23} data={parsedMetric} /></div>;
    $[51] = parsedMetric;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== metric.description || $[55] !== metric.descriptionPlainText) {
    t25 = metric.descriptionPlainText && <div className="mt-5 text-semantic-content-subtle leading-8"><RichText data={metric.description} /></div>;
    $[54] = metric.description;
    $[55] = metric.descriptionPlainText;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== inSideModal || $[58] !== metric.labels || $[59] !== router) {
    t26 = !!metric.labels?.length && <div className="flex gap-2 flex-wrap mt-6">{metric.labels.map(label => {
        const labelObj = typeof label === "object" ? label : null;
        if (!labelObj) {
          return null;
        }
        return <Chip onClick={() => {
          const url = `${SEARCH_CONSTANTS.SEARCH_BASE_URL}?${SEARCH_CONSTANTS.QUERY_PARAM}=${labelObj.name}`;
          if (inSideModal) {
            window.location.href = url;
            return;
          }
          router.push(url);
        }} key={labelObj.id}>{labelObj.name}</Chip>;
      })}</div>;
    $[57] = inSideModal;
    $[58] = metric.labels;
    $[59] = router;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== t25 || $[62] !== t26) {
    t27 = <div className="flex flex-col gap-2">{t25}{t26}</div>;
    $[61] = t25;
    $[62] = t26;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== t24 || $[65] !== t27) {
    t28 = <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-7 px-4 lg:px-10">{t24}{t27}</div>;
    $[64] = t24;
    $[65] = t27;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== t21 || $[68] !== t28 || $[69] !== t5) {
    t29 = <div className="space-y-4 lg:space-y-7 w-full max-w-content md:mx-auto grow">{t5}{t21}{t28}</div>;
    $[67] = t21;
    $[68] = t28;
    $[69] = t5;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== metric.dataSource) {
    t30 = metric.dataSource && <InfoBlock title="Allikas"><p>{metric.dataSource}</p></InfoBlock>;
    $[71] = metric.dataSource;
    $[72] = t30;
  } else {
    t30 = $[72];
  }
  let t31;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Skeleton isLoading={true} className="h-5 mt-1 w-44" />;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== metric.updatedAt) {
    t32 = formatDate(new Date(metric.updatedAt));
    $[74] = metric.updatedAt;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  let t33;
  if ($[76] !== t32) {
    t33 = <InfoBlock title="Viimati muudetud"><ClientOnly fallback={t31}><p>{t32}</p></ClientOnly></InfoBlock>;
    $[76] = t32;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] !== metric.responsibleInstitutionLink || $[79] !== metric.responsibleInstitutionName) {
    t34 = metric.responsibleInstitutionName && <InfoBlock title="Haldaja">{metric.responsibleInstitutionLink ? <Link external={true} href={metric.responsibleInstitutionLink}>{metric.responsibleInstitutionName}</Link> : <p>{metric.responsibleInstitutionName}</p>}</InfoBlock>;
    $[78] = metric.responsibleInstitutionLink;
    $[79] = metric.responsibleInstitutionName;
    $[80] = t34;
  } else {
    t34 = $[80];
  }
  let t35;
  if ($[81] !== metric.monitoringPeriod) {
    t35 = metric.monitoringPeriod && <InfoBlock title="Uuendamise intervall"><p>{metric.monitoringPeriod}</p></InfoBlock>;
    $[81] = metric.monitoringPeriod;
    $[82] = t35;
  } else {
    t35 = $[82];
  }
  let t36;
  if ($[83] !== metric.code) {
    t36 = metric.code && <InfoBlock title="ID"><p>{metric.code}</p></InfoBlock>;
    $[83] = metric.code;
    $[84] = t36;
  } else {
    t36 = $[84];
  }
  let t37;
  if ($[85] !== t30 || $[86] !== t33 || $[87] !== t34 || $[88] !== t35 || $[89] !== t36) {
    t37 = <div className="grid grid-cols-1 md:grid-cols-2 gap-x-2 gap-y-4 h-fit">{t30}{t33}{t34}{t35}{t36}</div>;
    $[85] = t30;
    $[86] = t33;
    $[87] = t34;
    $[88] = t35;
    $[89] = t36;
    $[90] = t37;
  } else {
    t37 = $[90];
  }
  let t38;
  if ($[91] !== metric.methodology || $[92] !== metric.methodologyPlainText) {
    t38 = metric.methodologyPlainText && <InfoBlock title="Metoodika"><RichText className="leading-7" data={metric.methodology} /></InfoBlock>;
    $[91] = metric.methodology;
    $[92] = metric.methodologyPlainText;
    $[93] = t38;
  } else {
    t38 = $[93];
  }
  let t39;
  if ($[94] !== media) {
    t39 = media?.url && <div className="shrink-0 lg:ml-auto"><Image className="max-w-28 lg:max-w-36" src={media.url} alt={media.alt ?? ""} width={media.width || 80} height={media.height || 80} priority={true} loading="eager" /></div>;
    $[94] = media;
    $[95] = t39;
  } else {
    t39 = $[95];
  }
  let t40;
  if ($[96] !== t38 || $[97] !== t39) {
    t40 = <div className="flex gap-4 md:gap-10 flex-col md:flex-row justify-between">{t38}{t39}</div>;
    $[96] = t38;
    $[97] = t39;
    $[98] = t40;
  } else {
    t40 = $[98];
  }
  let t41;
  if ($[99] !== t37 || $[100] !== t40) {
    t41 = <div className="bg-semantic-surface-faint flex justify-center"><div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-7 px-4 lg:px-10 py-5 w-full max-w-content">{t37}{t40}</div></div>;
    $[99] = t37;
    $[100] = t40;
    $[101] = t41;
  } else {
    t41 = $[101];
  }
  let t42;
  if ($[102] !== t29 || $[103] !== t41) {
    t42 = <NotificationProvider><div ref={containerRef} className="relative h-full flex flex-col gap-6 lg:gap-12 grow">{t4}{t29}{t41}</div></NotificationProvider>;
    $[102] = t29;
    $[103] = t41;
    $[104] = t42;
  } else {
    t42 = $[104];
  }
  return t42;
};